import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useUserAuth } from '../context/UserAuthContext';
import { 
  User, 
  CreditCard, 
  Activity, 
  Download,
  Keyboard,
  RefreshCw,
  ArrowUpRight
} from 'lucide-react';
import UserNavbar from '../components/UserNavbar';
import Footer from '../components/Footer';
import config from '../../config/env';

const UserDashboard = () => {
  const { user, fetchAccount } = useUserAuth();
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    fetchAccount();
  }, [fetchAccount]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchAccount();
    setIsRefreshing(false);
  };

  const steps = [
    { num: '01', text: 'Download and install the extension' },
    { num: '02', text: 'Enable Developer Mode in chrome://extensions' },
    { num: '03', text: 'Load unpacked and select the extension folder' },
    { num: '04', text: 'Use Ctrl+Shift+Space to open the assistant' },
  ];

  if (!user) {
    return (
      <div className="min-h-screen bg-[#111111] flex items-center justify-center">
        <div className="animate-pulse text-white/50">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#111111]">
      <UserNavbar />

      {/* Main Content */}
      <main className="pt-24 pb-20">
        <div className="px-6 md:px-12 lg:px-20">
          <div className="max-w-7xl mx-auto">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
              <div>
                <span className="text-white/40 text-xs font-mono tracking-widest mb-4 block">DASHBOARD</span>
                <h1 className="text-4xl md:text-5xl font-light text-white tracking-tight">
                  Welcome back, {user.username}
                </h1>
              </div>
              <div className="flex items-center gap-3">
                <button 
                  onClick={handleRefresh} 
                  disabled={isRefreshing}
                  className="inline-flex items-center gap-2 px-4 py-2 border border-white/20 text-white/70 hover:text-white hover:bg-white/5 transition-colors text-sm"
                >
                  <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                  Refresh
                </button>
                <Link 
                  to="/pricing"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-white text-black font-medium hover:bg-white/90 transition-colors text-sm"
                >
                  <CreditCard className="h-4 w-4" />
                  Buy Tokens
                </Link>
              </div>
            </div>

            {user.unreadNotificationCount > 0 && (
              <div className="mb-8 border border-green-500/30 bg-green-500/5 px-4 py-3 text-sm text-green-300">
                You have {user.unreadNotificationCount} unread {user.unreadNotificationCount === 1 ? 'notification' : 'notifications'}
              </div>
            )}

            {/* Stats Grid */}
            <div className="grid md:grid-cols-4 gap-px bg-white/10 mb-16">
              {/* Account */}
              <div className="bg-[#111111] p-8">
                <div className="flex items-center justify-between mb-6">
                  <span className="text-white/40 text-xs font-mono tracking-widest">ACCOUNT</span>
                  <User className="h-4 w-4 text-white/40" />
                </div>
                <div className="text-3xl font-light text-white mb-2">{user.username}</div>
                {/* <span className={`inline-flex items-center px-3 py-1 text-xs font-mono ${user.isPro ? 'text-accent border border-accent\/30' : 'text-white/50 border border-white/10'}`}>
                  {user.isPro ? 'PRO' : 'FREE'}
                </span> */}
              </div>

              {/* MCQ Credits */}
              <div className="bg-[#111111] p-8">
                <div className="flex items-center justify-between mb-6">
                  <span className="text-white/40 text-xs font-mono tracking-widest">MCQ CREDITS</span>
                  <CreditCard className="h-4 w-4 text-accent\/50" />
                </div>
                <div className="text-3xl font-light text-accent mb-2">
                  {user.mcqCredits?.toLocaleString() || 0}
                </div>
                <span className="text-white/50 text-sm">For MCQ questions</span>
              </div>

              {/* Coding Credits */}
              <div className="bg-[#111111] p-8">
                <div className="flex items-center justify-between mb-6">
                  <span className="text-white/40 text-xs font-mono tracking-widest">CODING CREDITS</span>
                  <CreditCard className="h-4 w-4 text-blue-400/50" />
                </div>
                <div className="text-3xl font-light text-blue-400 mb-2">
                  {user.codingCredits?.toLocaleString() || 0}
                </div>
                <span className="text-white/50 text-sm">For coding questions</span>
              </div>

              {/* Daily Usage */}
              <div className="bg-[#111111] p-8">
                <div className="flex items-center justify-between mb-6">
                  <span className="text-white/40 text-xs font-mono tracking-widest">DAILY USAGE</span>
                  <Activity className="h-4 w-4 text-white/40" />
                </div>
                <div className="text-3xl font-light text-white mb-2">
                  {user.dailyRequestsUsed || 0} / {user.dailyLimit || 100}
                </div>
                <div className="mt-4 w-full bg-white/10 h-1">
                  <div 
                    className="bg-accent-custom h-1 transition-all"
                    style={{ 
                      width: `${Math.min(((user.dailyRequestsUsed || 0) / (user.dailyLimit || 100)) * 100, 100)}%` 
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Two Column Layout */}
            <div className="grid lg:grid-cols-2 gap-16">
              {/* Download Extension */}
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-white/40 text-xs font-mono tracking-widest">EXTENSION</span>
                </div>
                <h2 className="text-3xl font-light text-white mb-4">Download Extension</h2>
                <p className="text-white/50 mb-8">
                  Download and install the extension to start using AI assistance in your browser.
                </p>
                <a 
                  href={config.extensionDownloadUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-white text-black font-medium hover:bg-white/90 transition-colors"
                >
                  <Download className="h-5 w-5" />
                  DOWNLOAD ZIP
                </a>
              </div>

              {/* Keyboard Shortcuts */}
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-white/40 text-xs font-mono tracking-widest">SHORTCUTS</span>
                </div>
                <h2 className="text-3xl font-light text-white mb-6">Keyboard Shortcuts</h2>
                <div className="space-y-px">
                  {config.shortcuts.map((shortcut, index) => (
                    <div 
                      key={index} 
                      className="flex items-center justify-between p-4 bg-white/[0.02] border border-white/10 hover:border-white/20 transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        {shortcut.keys.map((key, i) => (
                          <React.Fragment key={i}>
                            <kbd className="px-2 py-1 text-xs font-mono bg-white/5 text-white/70 border border-white/10">
                              {key}
                            </kbd>
                            {i < shortcut.keys.length - 1 && (
                              <span className="text-white/20">+</span>
                            )}
                          </React.Fragment>
                        ))}
                      </div>
                      <span className="text-white/50 text-sm">{shortcut.action}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* How to Use */}
            <div className="mt-20">
              <div className="flex items-center gap-3 mb-4">
                <span className="text-white/40 text-xs font-mono tracking-widest">GETTING STARTED</span>
              </div>
              <h2 className="text-3xl font-light text-white mb-10">How to Use NeoPass</h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10">
                {steps.map((item, index) => (
                  <div key={index} className="bg-[#111111] p-6 group">
                    <span className="text-2xl font-light text-white/20 group-hover:text-accent\/50 transition-colors font-mono block mb-4">
                      {item.num}
                    </span>
                    <p className="text-white/60 text-sm">{item.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer variant="full" />
    </div>
  );
};

export default UserDashboard;
